// Business-lens historic time-series pivot — PURE, framework-free, tested.
//
// Given the per-(account, month, currency) Xero revenue rows + the kyck-account
// DIMENSIONS (brand / group / industry / AM — from the account, NOT Xero's
// tracking categories), a dimension, a metric, a range and a currency, produce a
// CONTINUOUS monthly multi-series ready for Recharts:
//   - `months`  — every calendar month across the range (no collapsed/absent
//                 months; reuses the reporting continuous-domain enumerator);
//   - `rows`    — one row per month, each dimension value a keyed field, ADDITIVE
//                 0-fill for a month with no revenue (a genuine zero in position);
//   - `series`  — the ordered dimension values (total desc), each with a STABLE
//                 palette colour (assigned by display order) + its range total;
//   - `summary` — range-scoped headline totals for the stat cards.
// Money is per-currency (never cross-summed — the reporting money rule); the caller
// picks the currency. All values are integer CENTS (formatted to $ at the render
// edge). One shared core serves every chart on the page (the star time-series and
// any future minis), so none can diverge. Mirrors src/lib/xeroRevenueLens.ts +
// the reporting trendSeries pattern.

import { enumerateBucketDomain } from '../modules/reporting/chartDate.js';
import type { Industry } from '../types/account.js';
import type { AccountCountHistory } from '../types/accountAnalytics.js';
import type { XeroRevenueRow } from '../types/xero.js';
import { humanizeIndustry } from './accountAnalytics.js';
import { NO_AM_KEY, NO_BRAND_KEY, NO_GROUP_KEY } from './accountAnalyticsSnapshot.js';
import type { DateRange } from './reportingDateRange.js';
import { isExcludedAccountCode, UNCODED_ACCOUNT_CODE } from './xeroRevenue.js';
import type { RevenueDimension } from './xeroRevenueLens.js';

export type { RevenueDimension };

// The ways to slice the series. 'agency' = one whole-agency total line. 'code' =
// the Service-area dimension (per Xero account code / service line — Phase B).
export type BusinessSeriesDimension =
  | 'agency'
  | 'brand'
  | 'industry'
  | 'group'
  | 'am'
  | 'account'
  | 'code';

// The dimensions valid under the Account-counts metric (Phase C) — a per-account
// or per-code "count" is nonsensical, so service-area + account are excluded.
export type CountSeriesDimension = 'agency' | 'brand' | 'industry' | 'group' | 'am';

// Phase A ships accountIncome; recoupedMedia is wired for Phase B's metric switcher
// (its numerator is already on the wire row). Both are ADDITIVE (0-fill empties).
export type BusinessSeriesMetric = 'accountIncome' | 'recoupedMedia';

// The full metric switcher (Phase C adds accountCounts, which sources the snapshot
// series via pivotCountSeries rather than the revenue rows via pivotRevenueSeries).
export type BusinessLensMetric = BusinessSeriesMetric | 'accountCounts';

// A categorical palette for the multi-series lens. Dense-by-design (Scott's call —
// every dimension value gets a series; legend + filters navigate it, not top-N),
// so it carries more than the 5 on-brand chart vars — a curated, well-separated
// set, CYCLED when there are more series than colours. Self-contained (no CSS-var
// dependency) so the assignment is deterministic + unit-testable.
export const BUSINESS_SERIES_PALETTE: string[] = [
  '#3f9c53', // kyck green
  '#2f8fb3', // blue
  '#e0812f', // orange
  '#8e6fc4', // violet
  '#d1495b', // red
  '#2bb5a0', // teal
  '#c9a227', // gold
  '#7a8a99', // slate
  '#c2557f', // pink
  '#6aa84f', // leaf
  '#4a7fb5', // steel
  '#b5651d', // brown
  '#9b59b6', // purple
  '#16a085', // emerald
  '#d98880', // clay
  '#5d6d7e', // grey-blue
];

// The single series key the whole-agency total rolls under.
export const AGENCY_SERIES_KEY = '__agency__';

// One dimension value rendered as its own coloured series.
export interface BusinessSeries {
  key: string; // Recharts dataKey (opaque; unique per series — accountId for the account dim)
  label: string; // legend / tooltip label (the dimension value's display name)
  color: string; // stable palette colour (assigned by display order)
  totalCents: number; // Σ metric across the range — drives ordering + a legend readout
}

// One month row: the month plus each series key → its metric cents (0-filled).
export interface BusinessSeriesRow {
  month: string; // YYYY-MM (continuous domain)
  [seriesKey: string]: number | string;
}

// Range-scoped headline totals for the stat cards (metric-independent — always the
// income + recouped split, so the cards read the same whichever metric is charted).
export interface BusinessSeriesSummary {
  totalIncomeCents: number;
  totalRecoupedCents: number;
  accountCount: number; // distinct accounts with non-zero income in range
  monthCount: number; // distinct months with non-zero income in range
}

// The chart-ready shape both pivots share (the chart consumes only this).
export interface BusinessSeriesBase {
  months: string[]; // the continuous monthly domain
  rows: BusinessSeriesRow[]; // one per month, ordered chronologically
  series: BusinessSeries[]; // ordered by total desc, colour-assigned
}

export interface BusinessSeriesResult extends BusinessSeriesBase {
  summary: BusinessSeriesSummary;
}

export interface BusinessSeriesOptions {
  dimension: BusinessSeriesDimension;
  metric: BusinessSeriesMetric;
  range: DateRange;
  currency: string;
  // The active analytics filter's account set — restricts which accounts feed the
  // series (undefined = all). Same scoping seam the AM lens uses.
  accountIds?: Set<string>;
  // Xero account code → display name, for the 'code' (Service-area) dimension's
  // series labels. A code with no entry falls back to the bare code (the sentinel
  // uncoded key → "Uncoded"). Ignored by every other dimension.
  codeNames?: Map<string, string>;
}

// The (key, label) a row's account maps to for the chosen dimension. The account
// dimension keys by id (names can collide); every other dimension keys by its
// label (brand/group/industry/AM value), exactly like the AM×brand chart. The
// 'code' (Service-area) dimension is handled separately (a row splits across many
// codes), so it's excluded here.
function seriesForDimension(
  dim: RevenueDimension,
  accountId: string,
  dimension: Exclude<BusinessSeriesDimension, 'code'>,
): { key: string; label: string } {
  switch (dimension) {
    case 'agency':
      return { key: AGENCY_SERIES_KEY, label: 'Agency' };
    case 'brand':
      return { key: dim.brandLabel, label: dim.brandLabel };
    case 'industry':
      return { key: dim.industryLabel, label: dim.industryLabel };
    case 'group':
      return { key: dim.groupLabel, label: dim.groupLabel };
    case 'am':
      return { key: dim.amLabel, label: dim.amLabel };
    case 'account':
      return { key: accountId, label: dim.accountName };
  }
}

function metricCents(r: XeroRevenueRow, metric: BusinessSeriesMetric): number {
  return metric === 'recoupedMedia' ? r.mediaSpendRecoupedCents : r.accountIncomeCents;
}

// Whether a Xero account code belongs to the SELECTED metric's slice — the same
// classification rule as the headline figures (income = every non-excluded code;
// recoupedMedia = exactly the excluded 4140/4150 codes). No new classification.
function codeInMetric(code: string, metric: BusinessSeriesMetric): boolean {
  const excluded = isExcludedAccountCode(code);
  return metric === 'recoupedMedia' ? excluded : !excluded;
}

// Display label for a service-area (code) series: the chart-of-accounts name when
// known, a friendly "Uncoded" for the blank-code sentinel, else the bare code.
function labelForCode(code: string, codeNames?: Map<string, string>): string {
  const name = codeNames?.get(code);
  if (name) return name;
  return code === UNCODED_ACCOUNT_CODE ? 'Uncoded' : code;
}

// The earliest month present in the rows (optionally for one currency) — feeds the
// "All Time" preset's lower bound. null when there's no data.
export function earliestRevenueMonth(
  rows: XeroRevenueRow[],
  currency?: string,
): string | null {
  let min: string | null = null;
  for (const r of rows) {
    if (currency && r.currency !== currency) continue;
    if (min === null || r.month < min) min = r.month;
  }
  return min;
}

// Currencies present in the rows, income-desc (dominant first) then alpha — the
// currency toggle's options. (Kept here so the panel has one import surface; same
// logic as xeroRevenueLens.currenciesPresent.)
export function seriesCurrencies(rows: XeroRevenueRow[]): string[] {
  const totals = new Map<string, number>();
  for (const r of rows) {
    totals.set(r.currency, (totals.get(r.currency) ?? 0) + r.accountIncomeCents);
  }
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([c]) => c);
}

// A series accumulator: seriesKey → { label, running total, month → value }.
type SeriesAccum = Map<string, { label: string; total: number; byMonth: Map<string, number> }>;

function accumulate(acc: SeriesAccum, key: string, label: string, month: string, value: number): void {
  let s = acc.get(key);
  if (!s) {
    s = { label, total: 0, byMonth: new Map() };
    acc.set(key, s);
  }
  s.total += value;
  s.byMonth.set(month, (s.byMonth.get(month) ?? 0) + value);
}

// Turn an accumulator into the ordered, colour-assigned, continuous-domain series
// + rows. Ordered by total desc (label alpha tie-break) — the display order AND
// the stable colour-assignment order. Every month gets a row; absent buckets are
// 0-filled (additive). Shared by BOTH pivots so they can never diverge.
function finishSeries(acc: SeriesAccum, months: string[]): BusinessSeriesBase {
  const ordered = [...acc.entries()].sort(
    (a, b) => b[1].total - a[1].total || a[1].label.localeCompare(b[1].label),
  );
  const series: BusinessSeries[] = ordered.map(([key, s], i) => ({
    key,
    label: s.label,
    color: BUSINESS_SERIES_PALETTE[i % BUSINESS_SERIES_PALETTE.length]!,
    totalCents: s.total, // for counts this holds the total COUNT, not cents
  }));
  const rows: BusinessSeriesRow[] = months.map((month) => {
    const row: BusinessSeriesRow = { month };
    for (const [key, s] of ordered) row[key] = s.byMonth.get(month) ?? 0;
    return row;
  });
  return { months, rows, series };
}

// Pivot the revenue rows into a continuous monthly multi-series for one currency +
// dimension + metric. Rows are filtered to the currency, the account set, and the
// month range; an account with no dimension entry (e.g. a rollup for a since-
// deleted account) is skipped. Empty months are 0-filled per series (additive).
export function pivotRevenueSeries(
  rows: XeroRevenueRow[],
  dimByAccountId: Map<string, RevenueDimension>,
  opts: BusinessSeriesOptions,
): BusinessSeriesResult {
  const { dimension, metric, range, currency, accountIds, codeNames } = opts;
  const months = enumerateBucketDomain(range.from, range.to, 'monthly');
  const fromMonth = range.from.slice(0, 7);
  const toMonth = range.to.slice(0, 7);

  const acc: SeriesAccum = new Map();
  const addTo = (key: string, label: string, month: string, cents: number): void =>
    accumulate(acc, key, label, month, cents);

  let totalIncomeCents = 0;
  let totalRecoupedCents = 0;
  const accountsWithIncome = new Set<string>();
  const monthsWithIncome = new Set<string>();

  for (const r of rows) {
    if (r.currency !== currency) continue;
    if (accountIds && !accountIds.has(r.accountId)) continue;
    if (r.month < fromMonth || r.month > toMonth) continue; // 'YYYY-MM' sorts lexicographically
    const dim = dimByAccountId.get(r.accountId);
    if (!dim) continue;

    // Summary is income-based + metric-independent (the stat cards always split
    // income vs recouped, whichever metric the chart plots).
    totalIncomeCents += r.accountIncomeCents;
    totalRecoupedCents += r.mediaSpendRecoupedCents;
    if (r.accountIncomeCents !== 0) {
      accountsWithIncome.add(r.accountId);
      monthsWithIncome.add(r.month);
    }

    if (dimension === 'code') {
      // Service-area: a row splits across its per-code cents; each code is kept
      // only if it belongs to the selected metric's slice (income vs recouped),
      // keyed by the code, labelled via the chart-of-accounts name map.
      for (const [code, cents] of Object.entries(r.byCode ?? {})) {
        if (!codeInMetric(code, metric)) continue;
        addTo(code, labelForCode(code, codeNames), r.month, cents);
      }
    } else {
      const { key, label } = seriesForDimension(dim, r.accountId, dimension);
      addTo(key, label, r.month, metricCents(r, metric));
    }
  }

  return {
    ...finishSeries(acc, months),
    summary: {
      totalIncomeCents,
      totalRecoupedCents,
      accountCount: accountsWithIncome.size,
      monthCount: monthsWithIncome.size,
    },
  };
}

// ---- Phase C — account-count pivot -----------------------------------------

export interface CountSeriesOptions {
  dimension: CountSeriesDimension;
  range: DateRange;
}

// The (label) a count series key resolves to. brand/group/AM resolve via the read
// payload's id→name maps (departed AM already handled there), the count sentinels
// fall back to the same "(no …)" / "Unassigned" labels the revenue pivot uses, and
// industry is humanised from its enum. Agency is handled inline (single series).
function countLabel(
  dimension: Exclude<CountSeriesDimension, 'agency'>,
  key: string,
  history: AccountCountHistory,
): string {
  switch (dimension) {
    case 'brand':
      return history.brandNames[key] ?? (key === NO_BRAND_KEY ? '(no brand)' : key);
    case 'group':
      return history.groupNames[key] ?? (key === NO_GROUP_KEY ? '(no group)' : key);
    case 'am':
      return history.amNames[key] ?? (key === NO_AM_KEY ? 'Unassigned' : key);
    case 'industry':
      return humanizeIndustry(key as Industry);
  }
}

// The earliest snapshot month in the history — feeds the "All Time" preset's lower
// bound under the counts metric. null when there are no snapshots.
export function earliestCountMonth(history: AccountCountHistory): string | null {
  let min: string | null = null;
  for (const p of history.points) if (min === null || p.month < min) min = p.month;
  return min;
}

// Pivot the account-count snapshot series into the SAME continuous monthly
// multi-series as the revenue pivot (reuses the domain enumeration, additive
// 0-fill, all-series ordering + palette). No currency (counts aren't money). A
// month with no snapshot is a continuous-domain gap (0-filled). The 'agency'
// dimension is the single active-operational total; the others split by their
// pre-aggregated id-keyed count map, labelled via the read's name maps.
export function pivotCountSeries(
  history: AccountCountHistory,
  opts: CountSeriesOptions,
): BusinessSeriesBase {
  const { dimension, range } = opts;
  const months = enumerateBucketDomain(range.from, range.to, 'monthly');
  const fromMonth = range.from.slice(0, 7);
  const toMonth = range.to.slice(0, 7);

  const acc: SeriesAccum = new Map();
  for (const p of history.points) {
    if (p.month < fromMonth || p.month > toMonth) continue;
    if (dimension === 'agency') {
      accumulate(acc, AGENCY_SERIES_KEY, 'Agency', p.month, p.totalActiveOperational);
      continue;
    }
    const map =
      dimension === 'brand'
        ? p.byBrand
        : dimension === 'industry'
          ? p.byIndustry
          : dimension === 'group'
            ? p.byGroup
            : p.byAm;
    for (const [key, count] of Object.entries(map)) {
      accumulate(acc, key, countLabel(dimension, key, history), p.month, count);
    }
  }

  return finishSeries(acc, months);
}
